import { memo, type ReactNode } from "react";
import type { UseFormReturn } from "react-hook-form";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Form } from "@/shared/components/ui/form";
import { WizardStepper, type WizardStepDef } from "./WizardStepper";
import type { RentalCreateFormSchema } from "../rental-create-schema";

interface WizardShellProps {
  form: UseFormReturn<RentalCreateFormSchema>;
  steps: WizardStepDef[];
  step: number;
  maxReached: number;
  isSubmitting: boolean;
  onStepClick: (index: number) => void;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
  summary: ReactNode;
  children: ReactNode;
}

export const WizardShell = memo(function WizardShell({
  form,
  steps,
  step,
  maxReached,
  isSubmitting,
  onStepClick,
  onBack,
  onNext,
  onSubmit,
  summary,
  children,
}: WizardShellProps) {
  const isLastStep = step === steps.length - 1;

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <WizardStepper
              steps={steps}
              current={step}
              maxReached={maxReached}
              onStepClick={onStepClick}
            />
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
          <Card>
            <CardContent className="pt-6 space-y-6">
              {children}

              <div className="flex items-center justify-between pt-4 border-t">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onBack}
                  disabled={step === 0}
                  className="gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </Button>
                {isLastStep ? (
                  <Button
                    type="button"
                    onClick={onSubmit}
                    disabled={isSubmitting}
                    className="gap-2 min-w-40"
                  >
                    <Check className="w-4 h-4" />
                    {isSubmitting ? "Salvando..." : "Salvar aluguel"}
                  </Button>
                ) : (
                  <Button type="button" onClick={onNext} className="gap-2">
                    Próximo
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {summary}
        </div>
      </form>
    </Form>
  );
});
